import { useState, useMemo, useReducer, useCallback, useEffect } from 'react';
import { useAuth } from '../../../utils/Auth';
import ScheduleForm from './ScheduleForm';
import SelectOption from './SelectOption';
import { generateId } from '../../../lib/generateId';
import cloneDeep from 'lodash.clonedeep';
import { validateSchedule } from '../../../lib/validateSchedule';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import { useWindowSize} from '../../../utils/hooks/useWindowSize';
import styles from './QrRules.module.css';
import RuleName from './RuleName';
import RulesList from './RulesList';

// Main component
export default function QrRules() {

    // default structure/values for a new schedule
    const defaultSchedule = useMemo(() => ({
        active: true,
        createdDate: '',
        day: { enabled: false, type: 'day', value: [] },
        device: { enabled: false, value: '', type: 'device' },
        id: '',
        lastUpdated: '',
        name: '',
        resources: { data: [], type: '' },
        time: { enabled: false, type: 'time', value: { end: '', start: '', timezone: '' } },
        version: '1',
    }), []);

    // UI modes for the reducer that follows
    const scheduleMode = useMemo(() => ({
        EDITING: 'EDITING',          
        CREATING: 'CREATING',        
        // no specific action
        NONE: 'NONE'
    }),[]);

    // reducer to handle ui mode transitions
    const uiReducer = useCallback((state, action) => {
        switch (action.type) {
            case 'START_CREATE':
                return { mode: scheduleMode.CREATING };
            case 'START_EDIT':
                return { mode: scheduleMode.EDITING };
            case 'CLEANUP': 
                return { mode: scheduleMode.NONE };
            default:
                return state;
        }
    }, [scheduleMode]);

    // hooks and external data
    const { qr_data, dataDispatch, fileHandler } = useAuth();
    const { setFiles } = fileHandler;
    const { width } = useWindowSize();

    const [ uiState, uiDispatch ] = useReducer(uiReducer, { mode: scheduleMode.NONE });

    // only used to display year in calendar icon
    const currentYear = useMemo(() => new Date().getFullYear(), []);

    // states for simpler render logic
    const isEditingOrCreating = uiState.mode === scheduleMode.EDITING || uiState.mode === scheduleMode.CREATING;
    const isDeleteConfirm = uiState.mode === scheduleMode.DELETING;

    const [ error, setError ] = useState({
        hasError: false,
        message: '',
        validityResults: [],
    });

    const [ scheduleId, setScheduleId ] = useState('');
    const [ originalSchedule, setOriginalSchedule ] = useState({});

    const schedulesObject = useMemo(() => qr_data.data.schedule || {}, [qr_data?.data?.schedule]);
    const activeSchedulesArr = useMemo(() => Object.values(schedulesObject) || [], [schedulesObject]);

    // the schedule object currently being worked on (live reference for editing, or default values)
    const currentSchedule = useMemo(() => {
        return schedulesObject[scheduleId] 
            ? schedulesObject[scheduleId]
            : cloneDeep(defaultSchedule);
    }, [scheduleId, schedulesObject, defaultSchedule]);

    // generates unique ID and ensures it doesn't conflict with existing schedules
    const generateUniqueScheduleId = useCallback(() => {
        const id = generateId(8);
        if (qr_data?.data?.schedule?.[id]) {
            return generateUniqueScheduleId();
        }
        return id;
    }, [qr_data?.data?.schedule]);

    // resets ui state and schedule tracking after an action (save, cancel, delete)
    const cleanupForm = useCallback(() => {
        setScheduleId('');
        setOriginalSchedule({});
        setError({ hasError: false, message: '', validityResults: [] });
        uiDispatch({ type: 'CLEANUP' });
    }, []);

    // updates specific nested value in the active schedule via dataDispatch
    const handleUpdateScheduleField = useCallback(({ type = '', dataType = '', value }) => {
        if (!type || !dataType || (value === undefined || value === null)) {
            console.error(new Error('Type, dataType, or value is missing or invalid.'));
            return;
        }

        const val = value === 'none' ? '' : value;
        const sched = schedulesObject[scheduleId];
        
        if (sched) {
            dataDispatch({ 
                type: 'schedule', 
                data: { 
                    ...sched, 
                    [type]: { ...sched[type], [dataType]: val } 
                } 
            });
        }
    }, [dataDispatch, scheduleId, schedulesObject]);

    // process for new schedule rule
    const handleStartCreateSchedule = useCallback(() => {
        const newId = generateUniqueScheduleId();
        const defaultWithId = { ...cloneDeep(defaultSchedule), id: newId };
        
        setScheduleId(newId);
        setOriginalSchedule(defaultWithId);
        dataDispatch({ type: 'schedule', data: defaultWithId });
        uiDispatch({ type: 'START_CREATE' });
        setError({ hasError: false, message: '', validityResults: [] });
    }, [generateUniqueScheduleId, dataDispatch, defaultSchedule]);

    // process for editing an existing schedule rule
    const handleStartEditSchedule = useCallback((id) => {
        const foundSchedule = schedulesObject[id];
        
        if (!foundSchedule) {
            setError({ hasError: true, message: 'Could not find schedule to edit.', validityResults: [] });
            return;
        }

        setScheduleId(id);
        setOriginalSchedule(cloneDeep(foundSchedule)); 
        uiDispatch({ type: 'START_EDIT' });
        setError({ hasError: false, message: '', validityResults: [] });
    }, [schedulesObject]);

    // reverts changes when editing a rule or deletes unsaved rules when creating
    const handleCancelEdit = useCallback(() => {
        const current = schedulesObject[scheduleId];
        
        if (current?.createdDate) {
            dataDispatch({ type: 'schedule', data: originalSchedule });
        } else {
            dataDispatch({ type: 'schedule', data: { id: scheduleId, delete: true } });
        }
        
        cleanupForm();
    }, [dataDispatch, scheduleId, schedulesObject, originalSchedule, cleanupForm]);

    // process to delete a schedule and its resources (files)
    const handleConfirmDelete = useCallback((id) => {
        const scheduleToDelete = schedulesObject[id];

        if (!scheduleToDelete) return cleanupForm();

        if (['asset', 'pdf'].includes(scheduleToDelete?.resources?.type)) {
            const paths = scheduleToDelete.resources.data.map(f => f.path);
            setFiles(prev => ({ ...prev, deleteFiles: [...prev.deleteFiles, ...paths] }));
        }

        dataDispatch({ type: 'schedule', data: { id: id, delete: true } });
        
        cleanupForm();
    }, [dataDispatch, schedulesObject, setFiles, cleanupForm]);

    // validates the schedule and saves it
    const handleSaveSchedule = useCallback(async () => {
        if (!scheduleId) return;

        const scheduleToSave = schedulesObject[scheduleId];
        
        try {
            const validityErrorsArr = validateSchedule(schedulesObject, scheduleToSave.id) || [];

            if (validityErrorsArr.length === 0) {
                const date = new Date().toISOString();
                
                if (!scheduleToSave.createdDate) {
                    scheduleToSave.createdDate = date;
                }
                scheduleToSave.lastUpdated = date;

                dataDispatch({ type: 'schedule', data: scheduleToSave });
                cleanupForm();
            } else {
                setError({
                    hasError: true,
                    message: validityErrorsArr.map(err => err.message).join('\n'),
                    validityResults: validityErrorsArr,
                });
            }
        } catch (err) {
            console.error('Error saving schedule: ', err.message);
            setError({ hasError: true, message: err.message, validityResults: [], });
        }
    }, [scheduleId, schedulesObject, dataDispatch, cleanupForm]);

    const saveButtonText = useMemo(() => {
        if (uiState.mode === scheduleMode.CREATING) {
            return 'Save New Rule';
        }
        if (uiState.mode === scheduleMode.EDITING) {
            return 'Save Changes';
        }
        return 'Save';
    }, [uiState.mode, scheduleMode]);

    const headerText = useMemo(() => {
        if (uiState.mode === scheduleMode.CREATING) {
            return 'New Rule';
        }
        if (uiState.mode === scheduleMode.EDITING) {
            return 'Edit Rule';
        }
        return 'Rule Details'
    }, [uiState.mode, scheduleMode]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div className={styles.header}>Rules</div>
                <div className={styles.content}>
                    <div className={styles.left}>
                        <div className={styles.words}>
                            <span>Rules</span> determine when and where your QR code will be displayed or what content it will navigate to. 
                            Use the tool below to define your rules, ensuring your QR will only be active during the specific dates and times you set. 
                            This dynamic scheduling is essential for controlling the visibility and destination of your QR across different destinations or time periods.
                        </div>
                        <button 
                            className={styles.addScheduleBtn} 
                            onClick={handleStartCreateSchedule}  
                            disabled={isEditingOrCreating || isDeleteConfirm}
                        >
                            New Rule <i className='fa-regular fa-plus' />
                        </button>
                    </div>
                    <div className={styles.right}>
                        <motion.div
                            className={styles.calendar}
                            transition={transition}
                            variants={variants}
                            initial='qrInitial'
                            animate='qrAnimate'
                            custom={width}
                        >
                            <div>
                                <span>{currentYear}</span>
                                <i className='far fa-calendar-days' />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
            <div className={styles.bottomContent}>
                <div className={styles.left}>
                    <RulesList 
                        list={activeSchedulesArr} 
                        handleStartEditSchedule={handleStartEditSchedule} 
                        isEditingOrCreating={isEditingOrCreating} 
                        handleConfirmDelete={handleConfirmDelete} 
                        scheduleId={scheduleId}
                    />
                </div>
                <div className={styles.right}>
                    <AnimatePresence initial={false} mode='wait'>
                        {isEditingOrCreating && (
                            <motion.div 
                                className={styles.rulesContainer}
                                transition={transition}
                                variants={variants}
                                initial='initial'
                                animate='animate'
                                exit='initial'
                            >
                                <div className={styles.header}>{headerText}</div>
                                <RuleName schedule={currentSchedule} />
                                <SelectOption schedule={currentSchedule} />
                                <ScheduleForm
                                    schedule={currentSchedule} 
                                    handleSetSchedule={handleUpdateScheduleField} 
                                    error={error} 
                                />
                                <div className={styles.btnWrap}>
                                    <button className={styles.addScheduleBtn} onClick={handleSaveSchedule}>
                                        {saveButtonText} 
                                    </button>
                                    <button className={styles.cancelScheduleBtn} onClick={handleCancelEdit}>
                                        Cancel
                                    </button>
                                </div>
                                {error.hasError && (
                                    <div className={styles.error}>{error.message}</div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};